/**
 * N-255 · Qué foto admite el panel: la misma que admite el backend, y se dice AL ELEGIRLA.
 *
 * El backend rechaza con un 450 lo que no sea JPG, PNG o WebP (un AVIF incluido:
 * Cloudflare no lo transforma y la foto salía rota en el calendario del funnel), y PHP
 * corta lo que pase de `upload_max_filesize = 2M`. Pero ese 450 NO se ve en el panel
 * (N-171): «Guardar» no hacía nada. Decisión de Bernardo (6-oct-2026): el campo dice si
 * la foto vale ANTES de guardar, y nunca «sí» para luego «no».
 *
 * Por eso se mira lo mismo que mira el backend (`ImagenSubidaHelper::extensionDe`): el
 * CONTENIDO, no el nombre ni el tipo que da el navegador, que lo deduce del nombre. El
 * 5-oct entró un «.jpg» que era AVIF por dentro.
 */

/** `upload_max_filesize = 2M` del PHP del backend (sin php.ini propio: el de serie). */
export const MAXIMO_BYTES = 2 * 1024 * 1024;

/** Lo que lee el backend de cabecera: de sobra para el box `ftyp` más largo. */
const BYTES_CABECERA = 512;

type Formato = "jpg" | "png" | "webp" | "avif";

const ADMITIDOS: Formato[] = ["jpg", "png", "webp"];

function leerCabecera(file: Blob): Promise<Uint8Array> {
	return new Promise((resolve) => {
		const lector = new FileReader();
		lector.onload = () => resolve(new Uint8Array(lector.result as ArrayBuffer));
		// Lo que no se puede leer tampoco lo podría leer el backend: cuenta como vacío.
		lector.onerror = () => resolve(new Uint8Array(0));
		lector.readAsArrayBuffer(file.slice(0, BYTES_CABECERA));
	});
}

function texto(cabecera: Uint8Array, desde: number, largo: number): string {
	return String.fromCharCode(...Array.from(cabecera.subarray(desde, desde + largo)));
}

function empiezaPor(cabecera: Uint8Array, firma: number[]): boolean {
	return firma.every((byte, i) => cabecera[i] === byte);
}

/**
 * AVIF es ISO-BMFF: `ftyp` en los bytes 4-7, y detrás la marca principal, 4 de versión y
 * las compatibles, de 4 en 4. Es AVIF si `avif` o `avis` es cualquiera de ellas (igual que
 * `ImagenSubidaHelper::esAvif`); una HEIC (`heic`, `mif1`) no lo es.
 */
function esAvif(cabecera: Uint8Array): boolean {
	if (texto(cabecera, 4, 4) !== "ftyp") {
		return false;
	}
	const tam = ((cabecera[0] << 24) | (cabecera[1] << 16) | (cabecera[2] << 8) | cabecera[3]) >>> 0;
	if (tam < 16) {
		return false;
	}
	const fin = Math.min(tam, cabecera.length);
	const marcas = [texto(cabecera, 8, 4)];
	for (let i = 16; i + 4 <= fin; i += 4) {
		marcas.push(texto(cabecera, i, 4));
	}

	return marcas.indexOf("avif") !== -1 || marcas.indexOf("avis") !== -1;
}

/** El formato por el contenido, o null si no es ninguno de los cuatro conocidos. */
export async function formatoDe(file: Blob): Promise<Formato | null> {
	const cabecera = await leerCabecera(file);
	if (cabecera.length < 12) {
		return null;
	}
	if (empiezaPor(cabecera, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
		return "png";
	}
	if (empiezaPor(cabecera, [0xff, 0xd8, 0xff])) {
		return "jpg";
	}
	if (texto(cabecera, 0, 4) === "RIFF" && texto(cabecera, 8, 4) === "WEBP") {
		return "webp";
	}
	if (esAvif(cabecera)) {
		return "avif";
	}

	return null;
}

function megas(bytes: number): string {
	return (Math.round((bytes / (1024 * 1024)) * 10) / 10).toString().replace(".", ",");
}

/** Por qué no vale la foto, o null si vale (o si no hay foto: se ha borrado). */
export async function motivoDeRechazo(file: File | null | undefined): Promise<string | null> {
	if (!file) {
		return null;
	}
	const nombre = "«" + file.name + "»";
	const formato = await formatoDe(file);
	if (formato === "avif") {
		return nombre + " es una foto AVIF por dentro. Solo se aceptan JPG, PNG o WebP: expórtala antes a JPG.";
	}
	if (!formato || ADMITIDOS.indexOf(formato) === -1) {
		return nombre + " no es una imagen JPG, PNG ni WebP. Solo se aceptan esos tres formatos.";
	}
	if (file.size > MAXIMO_BYTES) {
		return nombre + " pesa " + megas(file.size) + " MB y el máximo es 2 MB. Redúcela antes de subirla.";
	}

	return null;
}
